import React, {useContext, useMemo} from 'react';
import {useFormContext} from 'react-hook-form';
import FormField, {Props} from '@src/components/Form/FormField';
import OrderProductsExtraRow from '@views/orders/fields/products/extra_row/OrderProductsExtraRow';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import {FormFieldItemPropsContext} from '@src/contexts/FormFieldItemPropsContext';
import {IOrderProductModel} from '@src/@interfaces/models/Order/IOrderProductModel';
import {IFormFieldItemPropsContext} from '@src/@interfaces/contexts/IFormFieldItemPropsContext';
import {useQueryClient} from '@tanstack/react-query';
import {refetchAllOrderQueriesExceptDisabled} from '@views/orders/refetchAllOrderQueriesExceptDisabled';
import app from '@src/helpers/app';

function ActualForm(props: Props<IOrderProductModel>) {
    const [designContext] = useContext(FormFieldDesignContext);
    const {control} = useFormContext();

    const isDisabled = props.forceDisabled || designContext.fieldsAreDisabled || designContext.fieldsAreReadOnly;

    const queryClient = useQueryClient();
    const [formFieldArrayContextValues, refresh] = useMemo(() => {
        const contextValue: IFormFieldItemPropsContext = {
            control,
            fieldName: props.fieldName,
            formName: props.formName,
            tabName: props.tabName,
            model: props.model,
            modelOptions: props.modelOptions,
            config: props.config,
            forceReadonly: props.config.readOnly === true || props.forceReadonly === true,
            forceDisabled: props.config.disabled === true || props.forceDisabled === true,
        };

        const refresh = () => {
            const key = app('queryKeys').getAllQueriesOfExactModelQueryKey(
                    'OrderProducts', props.model?.id
            );

            refetchAllOrderQueriesExceptDisabled(queryClient, key).then();
        };

        return [contextValue, refresh];
    }, [
        control,
        props.config,
        props.fieldName,
        props.forceDisabled,
        props.forceReadonly,
        props.formName,
        props.model,
        props.modelOptions,
        props.tabName,
        queryClient,
    ]);

    const model = props.model;
    if (!model) {
        return null;
    }

    return <FormFieldItemPropsContext.Provider value={formFieldArrayContextValues}>
        <OrderProductsExtraRow<IOrderProductModel>
                orderId={model.order_id}
                model={{...model?.order, id: model.order_id, products: [model]}}
                rowRecord={model}
                isDisabled={isDisabled}
                rowIndex={0}
                rowId={String(model.id)}
                onExcuseSpecified={refresh}
                onRefreshListOfFiles={refresh}
                listOfFilesUploaderContextControllerName="OrderProducts"
        />
    </FormFieldItemPropsContext.Provider>;
}

export default function OrderProductFormBody<Model extends IOrderProductModel = IOrderProductModel>(props: Props<Model>) {
    return <FormField {...props} withoutLayout={true} fieldElement={ActualForm as any}/>;
}
